import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

/**
 * Theme system — supports "light" and "dark".
 * The chosen theme is applied via `data-theme` attribute on <html>
 * and persisted in localStorage under `dr.theme`.
 *
 * To add new themes later: just add new [data-theme="X"] blocks in styles/tokens.css
 * and include the key in the THEMES array below.
 */

const STORAGE_KEY = 'dr.theme';
export const THEMES = ['dark', 'light'];

export const ThemeContext = createContext({
  theme: 'light',
  setTheme: () => {},
  toggleTheme: () => {},
  resolved: 'light',
});

function readStoredTheme() {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    return THEMES.includes(v) ? v : null;
  } catch { return null; }
}

function applyTheme(theme) {
  if (typeof document === 'undefined') return;
  document.documentElement.setAttribute('data-theme', theme);
  document.documentElement.setAttribute('data-bs-theme', theme);
  const favicon = document.getElementById('app-favicon');
  if (favicon) favicon.href = theme === 'dark' ? '/icon-32-white.png' : '/icon-32.png';
}

/**
 * Theme switch = full-page crossfade. View Transitions snapshot the page and
 * fade old→new, so hard swaps (logo src, chart SVG colors, images) change in
 * the same calm motion as token colors. Fallback: a short-lived class that
 * transitions color-bearing properties on every element. Reduced-motion
 * users get an instant switch.
 */
function startThemeTransition(apply) {
  if (typeof document === 'undefined') { apply(); return; }
  if (window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches) { apply(); return; }
  if (typeof document.startViewTransition === 'function') {
    document.startViewTransition(apply);
    return;
  }
  const root = document.documentElement;
  root.classList.add('theme-switching');
  apply();
  window.setTimeout(() => root.classList.remove('theme-switching'), 340);
}

export function ThemeProvider({ children, defaultTheme = 'light' }) {
  const [theme, setThemeState] = useState(() => {
    return readStoredTheme() || defaultTheme;
  });

  const appliedOnce = useRef(false);
  useEffect(() => {
    if (!appliedOnce.current) {
      // Initial mount: apply instantly — animating the first paint would flash.
      appliedOnce.current = true;
      applyTheme(theme);
    } else {
      startThemeTransition(() => applyTheme(theme));
    }
    try { localStorage.setItem(STORAGE_KEY, theme); } catch {}
  }, [theme]);

  const setTheme = useCallback((next) => {
    if (THEMES.includes(next)) setThemeState(next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState(prev => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme, resolved: theme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  return useContext(ThemeContext);
}
